/**
 * How a scoresheet cell should read a play code.
 *
 * The codes come from ball-score-server (see its play-code.ts) and drive the
 * cell's colour. A code this does not know renders as '?', so the two lists
 * have to stay in step — which is why this is a plain function with its own
 * checks in scripts/verify-play-outcomes.ts rather than a private method.
 */
export type PlayOutcome = 'hit' | 'out' | 'walk' | 'hbp' | 'error' | 'fc' | 'sac' | 'unknown';

export function playOutcome(play: string | undefined | null): PlayOutcome {
  const upper = String(play ?? '').trim().toUpperCase();
  if (!upper) return 'unknown';

  if (upper === 'HR' || /^H[1-9]?$/.test(upper) || upper === '1B'
      || /^2B\d?$/.test(upper) || /^D\d?$/.test(upper)
      || /^3B\d?$/.test(upper) || /^T\d?$/.test(upper)) {
    return 'hit';
  }
  if (upper === 'BB' || upper === 'IBB' || upper === 'W') return 'walk';
  if (upper === 'HBP' || upper === 'HP') return 'hbp';
  if (upper === 'FC' || upper === 'FCSH') return 'fc';
  // Obstruction awards the base, and it is the defence's doing — the same
  // family as an error as far as the cell's colour is concerned.
  if (upper === 'OB' || upper.startsWith('E')) return 'error';
  if (upper.startsWith('SAC') || upper === 'SF' || upper.startsWith('SH')) return 'sac';
  if (upper.startsWith('K')
      // A fielding code may carry a two-letter prefix: FP3 is a foul pop, FF2 a
      // caught foul fly. Bare 'L' and 'P' are the server's fallback when a
      // lines-out or pops-out line carries no code at all.
      || /^(FF|FP|SF|[FLPG])\d/.test(upper) || upper === 'L' || upper === 'P'
      || /^\d+(-\d+)*$/.test(upper) || /^G?DP/.test(upper)
      // A dropped infield fly: the batter is out by rule.
      || upper === 'DIF') {
    return 'out';
  }
  // An out by rule, whose code the server takes from WBSC verbatim so that a
  // rule nobody here has seen still reaches the cell — which means this list
  // can never enumerate them. Seen on real games: DIF, KSB, BOT (batting out of
  // turn), TBB (touched batted ball), BIN. Every one of them is an out, and
  // rendering it as '?' turned a rule this side does not know into what looks
  // like a parser failure.
  //
  // Last, on purpose: BB, HP, FC, SF, OB and IBB are all two or three letters
  // and are answered above.
  if (/^[A-Z]{2,4}$/.test(upper)) return 'out';
  return 'unknown';
}
